import { useAuthContext } from "../../auth/state/AuthContext";
import { Vessel } from "../../documents/models/documents.models";
import { themeAtom, userAtom } from "../../shared/atoms";
import { log, logV } from "../../shared/services/debug-logger";
import { ReusableButton } from "../../voyage-pnl/components/ReusableTableButton";
import { VOYAGE_ASSESMENT_QUESTIONS } from "../models/voyage-assesment-questions.const";
import type { FormStatus, Question, VoyageAssesmentFormM } from "../models/voyage-assesment.models";
import { saveVoyageAssesmentForm } from "../services/voyage-assesment.service";
import AssesmentCompleted from "./AssesmentCompleted";
import RatingRow from "./RatingRow";
import { datadogRum } from "@datadog/browser-rum";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";

type Props = {
  vessel: Vessel | undefined;
  initialFormStatus: FormStatus;
};

export const VoyageAssesmentForm = ({ vessel, initialFormStatus }: Props) => {
  const theme = useAtomValue(themeAtom);

  const { tcCode, voyageNo } = useParams();
  const user = useAtomValue(userAtom);
  const [questions, setQuestions] = useState<Question[]>(VOYAGE_ASSESMENT_QUESTIONS);
  const [formPristine, setFormPristine] = useState<boolean>(true);
  const [formStatus, setFormStatus] = useState<FormStatus>(initialFormStatus);
  const [form, setForm] = useState<VoyageAssesmentFormM>({
    vesselCode: vessel?.code ?? "",
    vesselName: vessel?.name ?? "",
    imoNumber: vessel?.imoNumber ?? "",
    tcCode: tcCode ?? "",
    voyageNo: voyageNo ?? "",
    submittedBy: user?.name ?? "",
  });
  const showFeedbackBox = formStatus === "done" || formStatus === "sent";
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const navigate = useNavigate();
  const goBack = () => {
    log("VoyageAssesmentForm - goBack");
    navigate(-1);
  };

  const updateQuestionRating = (questionId: string, newRating: number) => {
    setQuestions((prevQuestions) => prevQuestions.map((q) => (q.id === questionId ? { ...q, rating: newRating } : q)));

    setForm((prevForm) => ({
      ...prevForm,
      [questionId]: newRating,
    }));
  };

  const validateForm = (form: VoyageAssesmentFormM): boolean => {
    if (formPristine === true) {
      setFormPristine(false);
    }

    return (
      !!form.flexibility &&
      !!form.promptnessAndCoorperation &&
      !!form.transparencyAndComunication &&
      !!form.crew &&
      !!form.fleetMaintenance
    );
  };

  const updateComment = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForm((prevForm) => ({ ...prevForm, comment: event.target.value }));
  };

  logV("VoyageAssesmentForm - BUILD", { status: formStatus });

  return (
    <div data-test="voyage-feedback-form">
      {showFeedbackBox ? (
        <AssesmentCompleted status={formStatus} tcCode={tcCode} voyageNo={voyageNo} />
      ) : (
        <>
          <div data-test="voyage-ratings" className="flex flex-col gap-8">
            {questions.map((question) => {
              let error: string;
              if (!formPristine && !form[question.id]) {
                error = "Please provide a rating";
              }

              return (
                <RatingRow
                  key={question.id}
                  title={question.title}
                  subtitle={question.subtitle}
                  rating={question.rating}
                  onClick={(newRating) => updateQuestionRating(question.id, newRating)}
                  error={error!}
                />
              );
            })}

            <div className="w-full">
              <div className="mb-2">
                <span className={twMerge("font-bold", theme.headerComponent.inline.generalText)}>
                  Additional comments
                </span>
                <span className={twMerge("ml-1 text-sm", theme.headerComponent.inline.generalText)}>(optional)</span>
              </div>
              <textarea
                className={twMerge(
                  "block w-full rounded border px-3 py-2.5 focus:outline-none",
                  theme.home.tailwind.pageBackground,
                  theme.home.tailwind.textDefault,
                  theme.uwhcAssistant.border,
                )}
                rows={5}
                placeholder="Please provide any additional feedback or observations regarding the vessel owner’s performance during this voyage."
                onChange={updateComment}
              />
            </div>
          </div>

          <div data-test="voyage-actions" className="mt-10 flex justify-end gap-4">
            <ReusableButton buttonText="Cancel and exit" onClickHandler={goBack} width={140} />

            <ReusableButton
              buttonText="Submit"
              buttonType="submit"
              onClickHandler={() => {
                const valid = validateForm(form);
                log("VoyageAssesmentForm - onClick - submit", { valid });
                if (valid) {
                  saveVoyageAssesmentForm(form, () => {
                    log("STATE - saveVoyageAssesmentForm - sent");
                    setFormStatus("sent");
                  });

                  datadogRum.addAction("sendVoyageAssessment", {
                    ext_user_email: email,
                    vesselCode: vessel?.code ?? "",
                    vesselName: vessel?.name ?? "",
                    imoNumber: vessel?.imoNumber ?? "",
                    tcCode: tcCode ?? "",
                    voyageNo: voyageNo ?? "",
                    submittedBy: user?.name ?? "",
                  });
                }
              }}
              width={100}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default VoyageAssesmentForm;
